import { signal } from "@angular/core";
import { Event } from "../../pojo/Event";
import { Item } from "../../pojo/Item";
import { MoreActionsService } from "../../standalone/more-actions/more.actions.service";
import { S25Const } from "../../util/s25-const";
import { StateService } from "../../services/state.service";
import { FlsService } from "../../services/fls.service";
import { EventService } from "../../services/event.service";
import { S25Util } from "../../util/s25-util";
import { LangService } from "../../services/lang.service";
import { ItemUtil } from "../s25-item/item.util";

interface ContextMenuBean {
    item: { itemId: number; itemTypeId: number };
    lang: LangService.Language;
    subPerms: any;
    itemUrl: string;
    openInNewTab: () => void;
    details: () => Promise<void>;
    calendar: () => Promise<void>;
    list: () => Promise<void>;
    availability: () => Promise<void>;
    emailEvent: () => Promise<void>;
    editEvent: () => Promise<void>;
    copyEvent: () => Promise<void>;
    availabilityWeekly: () => Promise<void>;
}

export class ContextMenuService {
    private static contextMenuInstance = signal<{ close: () => void } | undefined>(undefined);

    private static type2SubPerms: {
        [key in ItemUtil.SupportedItemType]?: (itemId: number) => Promise<Event.Permissions.CopyEditEmail>;
    } = {
        1: async (itemId: number) => await EventService.formEventCopyEditEmailPermsFromId(itemId),
    };

    public static registerInstance(component: { close: () => void }) {
        // if one was already open, close it!
        this.contextMenuInstance()?.close();
        this.contextMenuInstance.set(component);
    }

    public static unregisterInstance(instance: unknown) {
        if (this.contextMenuInstance() === instance) {
            this.contextMenuInstance.set(undefined);
        }
    }

    public static closeMenu() {
        this.contextMenuInstance()?.close();
        this.contextMenuInstance.set(undefined);
    }

    public static async hydrateMenu(
        itemTypeId: ItemUtil.SupportedItemType,
        itemId: number,
        bean?: any,
        itemExt?: any,
    ): Promise<{ data: ContextMenuBean } | undefined> {
        bean ??= {};
        itemExt = { ...itemExt };
        itemTypeId = S25Const.itemName2Id[itemTypeId] ?? itemTypeId;
        itemTypeId = StateService.ItemTypeNorm[itemTypeId] ?? itemTypeId;

        const [fls, lang] = await Promise.all([FlsService.getFls(), LangService.getLang()]);

        const hasPerms =
            fls &&
            !(itemTypeId === 4 && fls.SPACE_LIST === "N") &&
            !(itemTypeId === 6 && fls.RESOURCE_LIST === "N") &&
            !(itemTypeId === 2 && fls.CU_ACCOUNT === "N");

        if (hasPerms) {
            const item = { ...itemExt, itemId, itemTypeId };

            const subPermsData = {
                ...((await ContextMenuService.type2SubPerms[itemTypeId]?.(itemId)) ?? {}),
                hasOpenInNewTab: !S25Util.mobileCheck(),
            };

            // Preserve bean object reference and any caller-provided data fields (e.g. availability
            // grid perms / deleteRsrv / copyRsrv / rsrvDetails), matching AngJS angular.merge.
            Object.assign(bean, {
                hideEventParams: { event: "unfocus" },
                data: {
                    ...bean.data,
                    item: item,
                    lang: lang,
                    subPerms: {
                        ...bean.data?.subPerms,
                        ...subPermsData,
                    },
                    itemUrl: `#!/home/${S25Const.itemId2Name[itemTypeId]}/${itemId}/details`, //used for open in new tab link
                    openInNewTab: this.closeMenuAnd((): void => {}),
                    details: this.closeMenuAnd(() =>
                        //quick jump to object details
                        StateService.gotoItem({
                            ...item,
                            forceView: "details",
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        }),
                    ),
                    calendar: this.closeMenuAnd(() =>
                        //quick jump to object calendar
                        StateService.gotoItem({
                            ...item,
                            forceView: "calendar",
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        }),
                    ),
                    list: this.closeMenuAnd(() =>
                        //quick jump to object list
                        StateService.gotoItem({
                            ...item,
                            forceView: "list",
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        }),
                    ),
                    availability: this.closeMenuAnd(() =>
                        //quick jump to object availability
                        StateService.gotoItem({
                            ...item,
                            forceView: "availability",
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        }),
                    ),
                    availabilityWeekly: this.closeMenuAnd(() =>
                        //quick jump to object availabilityWeekly
                        StateService.go(
                            "locationAvailabilityWeekly",
                            {
                                itemTypeId: item.itemTypeId,
                                itemId: item.itemId,
                                startDt: S25Util.date.toS25ISODateStr(new Date(bean?.startDt ?? Date.now())),
                                weeks: bean?.weeks || 1,
                                dows: bean?.dows || [
                                    "Sunday",
                                    "Monday",
                                    "Tuesday",
                                    "Wednesday",
                                    "Thursday",
                                    "Friday",
                                    "Saturday",
                                ],
                            },
                            {},
                        ),
                    ),
                    emailEvent: this.closeMenuAnd(() =>
                        StateService.gotoItem({
                            ...item,
                            forceView: "details",
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        }).then(() => {
                            setTimeout(() => {
                                MoreActionsService.open("email");
                            });
                        }),
                    ),
                    editEvent: this.closeMenuAnd(() => {
                        if (subPermsData.hasRelatedEvents) {
                            return ContextMenuService.openCopyEditEventModal(item.itemId, "edit");
                        }
                        return StateService.gotoRose({
                            ...item,
                            itemTypeId: item.itemTypeId === Item.AvailabilityItemTypeId ? 1 : item.itemTypeId,
                        });
                    }),
                    copyEvent: this.closeMenuAnd(() =>
                        //note: always open copy modal when copying event
                        ContextMenuService.openCopyEditEventModal(item.itemId, "clone"),
                    ),
                } satisfies ContextMenuBean,
            });

            return bean;
        }
    }

    private static closeMenuAnd<T>(then: () => T) {
        return () => {
            this.closeMenu();
            return then();
        };
    }

    private static openCopyEditEventModal(eventId: number, type: "edit" | "clone") {
        return Promise.all([
            import("../../standalone/more-actions/edit/modal.copy.edit.event.component"),
            import("../modal/modal.service"),
        ]).then(([{ ModalCopyEditEventComponent }, { ModalService }]) =>
            ModalService.byClass(ModalCopyEditEventComponent, { eventId, type }),
        );
    }
}
